import { gql } from 'graphql-request';
import {
  ProductAssociatedMaterialID,
  ProductBlocked,
  ProductBrandID,
  ProductBrandName,
  ProductCategoryID,
  ProductCategoryName,
  ProductClientCurrentTeamNanoId,
  ProductClientFullName,
  ProductClientID,
  ProductClientNanoId,
  ProductCollectionID,
  ProductCollectionName,
  ProductColorID,
  ProductColorName,
  ProductCustomTagID,
  ProductCustomTagName,
  ProductCustomTagNameEn,
  ProductTeamCustomTagID,
  ProductTeamCustomTagName,
  ProductTeamCustomTagUUID,
  ProductCustomTagUUID,
  ProductFavorite,
  ProductFileVersionTagID,
  ProductFileVersionTagName,
  ProductID,
  ProductImageFile,
  ProductImageID,
  ProductImageName,
  ProductImageSize,
  ProductImageUUID,
  ProductLifestyleID,
  ProductMaterialID,
  ProductMaterialName,
  ProductMaxFileCreatedAt,
  ProductMaxFileFile,
  ProductMaxFileID,
  ProductMaxFileName,
  ProductMaxFileSize,
  ProductMaxFileUUID,
  ProductMistakeTypeID,
  ProductMistakeTypeName,
  ProductName,
  ProductNanoID,
  ProductNda,
  ProductProductTypeID,
  ProductProductTypeName,
  ProductRenderTypeID,
  ProductRenderTypeName,
  ProductSeasonalID,
  ProductSeasonalName,
  ProductSelectedProductsCount,
  ProductSku,
  ProductStatusID,
  ProductStatusName,
  ProductStyleID,
  ProductStyleName,
  ProductTagID,
  ProductTagName,
  ProductTagNameEn,
  ProductTaskID,
  ProductTaskName,
  ProductTaskNanoID,
  ProductTeamID,
  ProductTeamName,
  ProductTeamNanoId,
  ProductTeamUUID,
  ProductUpdatedAt,
  ProductUUID,
  ProductUserFullName,
  ProductUserID,
  ProductUserNanoID
} from '../productsTypes';
import { SelectedProductUserCurrentTeamNanoID } from '../../selectedProducts/selectedProductsTypes';

export interface CacheFetchShowProductQueryResponseImage {
  id: ProductImageID;
  uuid: ProductImageUUID;
  file: ProductImageFile;
  size: ProductImageSize;
  name: ProductImageName;
}

export interface CacheFetchShowProductQueryResponseMaxFile {
  id: ProductMaxFileID;
  uuid: ProductMaxFileUUID;
  file: ProductMaxFileFile;
  size: ProductMaxFileSize;
  name: ProductMaxFileName;
}

export interface CacheFetchShowProductQueryResponse {
  id: ProductID;
  nanoId: ProductNanoID;
  uuid: ProductUUID;
  name: ProductName;
  sku: ProductSku;
  nda: ProductNda;
  image: CacheFetchShowProductQueryResponseImage;
  images?: CacheFetchShowProductQueryResponseImage[];
  maxFile: {
    createdAt: ProductMaxFileCreatedAt;
  };
  maxFiles?: CacheFetchShowProductQueryResponseMaxFile[];
  previewModel?: CacheFetchShowProductQueryResponseMaxFile;
  favorite: ProductFavorite;
  blocked: ProductBlocked;
  productCategory?: {
    id: ProductCategoryID;
    localizedName: ProductCategoryName;
  };
  brand?: {
    id: ProductBrandID;
    localizedName: ProductBrandName;
  };
  style?: {
    id: ProductStyleID;
    localizedName: ProductStyleName;
  };
  clients: {
    id: ProductClientID;
    nanoId: ProductClientNanoId;
    fullName: ProductClientFullName;
    currentTeam: {
      nanoId: ProductClientCurrentTeamNanoId;
    };
  }[];
  collections: {
    id: ProductCollectionID;
    localizedName: ProductCollectionName;
  }[];
  materials: {
    id: ProductMaterialID;
    localizedName: ProductMaterialName;
  }[];
  colors: {
    id: ProductColorID;
    localizedName: ProductColorName;
  }[];
  tags: {
    id: ProductTagID;
    nameEn: ProductTagNameEn;
    localizedName: ProductTagName;
  }[];
  statuses: {
    id: ProductStatusID;
    localizedName: ProductStatusName;
  }[];
  mistakeTypes: {
    id: ProductMistakeTypeID;
    localizedName: ProductMistakeTypeName;
  }[];
  fileVersionTags: {
    id: ProductFileVersionTagID;
    localizedName: ProductFileVersionTagName;
  }[];
  renderTypes: {
    id: ProductRenderTypeID;
    localizedName: ProductRenderTypeName;
  }[];
  lifestyles: {
    id: ProductLifestyleID;
  }[];
  productType: {
    id: ProductProductTypeID;
    localizedName: ProductProductTypeName;
  };
  seasonal: {
    id: ProductSeasonalID;
    localizedName: ProductSeasonalName;
  };
  task?: {
    id: ProductTaskID;
    nanoId: ProductTaskNanoID;
    name: ProductTaskName;
  };
  teams: {
    id: ProductTeamID;
    uuid: ProductTeamUUID;
    nanoId: ProductTeamNanoId;
    name: ProductTeamName;
  }[];
  customTags: {
    id: ProductCustomTagID;
    uuid: ProductCustomTagUUID;
    name: ProductCustomTagName;
    nameEn: ProductCustomTagNameEn;
  }[];
  teamCustomTags: {
    id: ProductTeamCustomTagID;
    uuid: ProductTeamCustomTagUUID;
    name: ProductTeamCustomTagName;
  }[];
  selectedProductsCount: ProductSelectedProductsCount;
  updatedAt: ProductUpdatedAt;
  associatedMaterials: {
    id: ProductAssociatedMaterialID;
  }[];
  user: {
    id: ProductUserID;
    nanoId: ProductUserNanoID;
    fullName: ProductUserFullName;
    currentTeam: {
      nanoId: SelectedProductUserCurrentTeamNanoID;
    };
  };
}

export const CACHE_FETCH_SHOW_PRODUCT_QUERY = gql`
  query CacheShowProduct($uuid: ID!, $source: String) {
    product(uuid: $uuid, source: $source) {
      id
      nanoId
      uuid
      name
      sku
      nda
      favorite
      blocked
      productType {
        id
        localizedName
      }
      seasonal {
        id
        localizedName
      }
      productCategory {
        id
        localizedName
      }
      brand {
        id
        localizedName
      }
      image {
        id
        uuid
        size
        name
        file
      }
      images {
        id
        uuid
        file
        size
        name
      }
      maxFile {
        createdAt
      }
      maxFiles {
        id
        uuid
        file
        size
        name
      }
      previewModel {
        id
        uuid
        file
        size
        name
      }
      style {
        id
        localizedName
      }
      teams {
        id
        nanoId
        uuid
        name
      }
      clients {
        id
        nanoId
        fullName
        currentTeam {
          nanoId
        }
      }
      collections {
        id
        localizedName
      }
      materials {
        id
        localizedName
      }
      colors {
        id
        localizedName
      }
      tags {
        id
        nameEn
        localizedName
      }
      statuses {
        id
        localizedName
      }
      mistakeTypes {
        id
        localizedName
      }
      fileVersionTags {
        id
        localizedName
      }
      renderTypes {
        id
        localizedName
      }
      lifestyles {
        id
      }
      task {
        id
        nanoId
        name
      }
      customTags {
        id
        uuid
        name
        nameEn
      }
      teamCustomTags {
        id
        uuid
        name
      }
      selectedProductsCount
      updatedAt
      associatedMaterials {
        id
      }
      user {
        id
        nanoId
        fullName
        currentTeam {
          nanoId
        }
      }
    }
  }
`;
